import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Clock, Users, UserCheck, ClipboardList, Mail, Printer, Download, Send, Upload, Image } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { API, CATEGORY_OPTIONS, formatDateTime } from './adminConstants';

// Activity Modal
export function ActivityModal({ 
    modal, 
    setModal, 
    onSave, 
    headers,
    uploadingImage,
    setUploadingImage
}) {
    const activityImageRef = useRef(null);

    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF');
            return;
        }
        
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File too large. Maximum size is 5MB');
            return;
        }
        
        setUploadingImage(true);
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axios.post(`${API}/upload/image`, formData, {
                headers: {
                    ...headers,
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
            setModal({ open: true, data: { ...modal.data, image_url: imageUrl }});
            toast.success('Image uploaded successfully');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload image');
        } finally {
            setUploadingImage(false);
        }
    };

    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {modal.data?.id ? 'Edit Activity' : 'Create New Activity'}
                    </DialogTitle>
                    <DialogDescription>
                        {modal.data?.id ? 'Update the activity details below.' : 'Fill in the details to create a new wellness activity.'}
                    </DialogDescription>
                </DialogHeader>
                
                {modal.data && (
                    <div className="space-y-4">
                        <div>
                            <Label>Title *</Label>
                            <Input
                                value={modal.data.title}
                                onChange={(e) => setModal({ ...modal, data: { ...modal.data, title: e.target.value } })}
                                placeholder="e.g., Gentle Yoga"
                                data-testid="activity-title-input"
                            />
                        </div>
                        
                        <div>
                            <Label>Description *</Label>
                            <Textarea
                                value={modal.data.description}
                                onChange={(e) => setModal({ ...modal, data: { ...modal.data, description: e.target.value } })}
                                placeholder="Describe the activity..."
                                rows={3}
                                data-testid="activity-desc-input"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Category *</Label>
                                <select
                                    value={modal.data.category}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, category: e.target.value } })}
                                    className="w-full h-10 rounded-md border border-input bg-background px-3 mt-1"
                                    data-testid="activity-category-select"
                                >
                                    {CATEGORY_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label>Credit Cost</Label>
                                <Input
                                    type="number"
                                    value={modal.data.credit_cost}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, credit_cost: parseInt(e.target.value) || 1 } })}
                                    min={0}
                                    data-testid="activity-credits-input"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Date & Time *</Label>
                                <Input
                                    type="datetime-local"
                                    value={modal.data.date_time ? modal.data.date_time.slice(0, 16) : ''}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, date_time: e.target.value } })}
                                    data-testid="activity-datetime-input"
                                />
                            </div>
                            <div>
                                <Label>End Time</Label>
                                <Input
                                    type="datetime-local"
                                    value={modal.data.end_time ? modal.data.end_time.slice(0, 16) : ''}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, end_time: e.target.value } })}
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Location</Label>
                                <Input
                                    value={modal.data.location}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, location: e.target.value } })}
                                    placeholder="e.g., Village Hall"
                                    data-testid="activity-location-input"
                                />
                            </div>
                            <div>
                                <Label>Capacity</Label>
                                <Input
                                    type="number"
                                    value={modal.data.capacity}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, capacity: parseInt(e.target.value) || 20 } })}
                                    min={1}
                                    data-testid="activity-capacity-input"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Label>Facilitator</Label>
                            <Input
                                value={modal.data.facilitator || ''}
                                onChange={(e) => setModal({ ...modal, data: { ...modal.data, facilitator: e.target.value } })}
                                placeholder="e.g., Sarah Johnson"
                                data-testid="activity-facilitator-input"
                            />
                        </div>
                        
                        <div>
                            <Label>Activity Image</Label>
                            <div className="flex items-center gap-4 mt-2">
                                {modal.data.image_url ? (
                                    <img 
                                        src={modal.data.image_url} 
                                        alt="Activity"
                                        className="w-20 h-20 object-cover rounded-lg border"
                                    />
                                ) : (
                                    <div className="w-20 h-20 rounded-lg bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center">
                                        <Image className="w-8 h-8 text-gray-400" />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        ref={activityImageRef}
                                        onChange={handleImageUpload}
                                        accept="image/jpeg,image/png,image/webp,image/gif"
                                        className="hidden"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => activityImageRef.current?.click()}
                                        disabled={uploadingImage}
                                        data-testid="upload-activity-image-btn"
                                    >
                                        {uploadingImage ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4 mr-2" />
                                                Upload Image
                                            </>
                                        )}
                                    </Button>
                                    <p className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP, GIF (max 5MB)</p>
                                </div>
                            </div>
                            <div className="mt-2">
                                <Label className="text-xs text-muted-foreground">Or enter URL directly</Label>
                                <Input
                                    value={modal.data.image_url || ''}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, image_url: e.target.value } })}
                                    placeholder="https://example.com/image.jpg"
                                    className="mt-1"
                                />
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={modal.data.is_free}
                                onCheckedChange={(checked) => setModal({ ...modal, data: { ...modal.data, is_free: checked } })}
                                data-testid="activity-free-toggle"
                            />
                            <Label>Free activity (no credits required)</Label>
                        </div>
                    </div>
                )}
                
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>
                        Cancel
                    </Button>
                    <Button onClick={onSave} data-testid="save-activity-btn">
                        {modal.data?.id ? 'Update' : 'Create'} Activity
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Event Modal
export function EventModal({ 
    modal, 
    setModal, 
    onSave, 
    headers,
    uploadingImage,
    setUploadingImage
}) {
    const eventImageRef = useRef(null);

    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF');
            return;
        }
        
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File too large. Maximum size is 5MB');
            return;
        }
        
        setUploadingImage(true);
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axios.post(`${API}/upload/image`, formData, {
                headers: {
                    ...headers,
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
            setModal({ open: true, data: { ...modal.data, image_url: imageUrl }});
            toast.success('Image uploaded successfully');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload image');
        } finally {
            setUploadingImage(false);
        }
    };

    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {modal.data?.id ? 'Edit Event' : 'Create New Event'}
                    </DialogTitle>
                    <DialogDescription>
                        {modal.data?.id ? 'Update the event details below.' : 'Fill in the details to create a new public event.'}
                    </DialogDescription>
                </DialogHeader>
                
                {modal.data && (
                    <div className="space-y-4">
                        <div>
                            <Label>Title *</Label>
                            <Input
                                value={modal.data.title}
                                onChange={(e) => setModal({ ...modal, data: { ...modal.data, title: e.target.value } })}
                                placeholder="e.g., Summer Garden Party"
                                data-testid="event-title-input"
                            />
                        </div>
                        
                        <div>
                            <Label>Description *</Label>
                            <Textarea
                                value={modal.data.description}
                                onChange={(e) => setModal({ ...modal, data: { ...modal.data, description: e.target.value } })}
                                placeholder="Describe the event..."
                                rows={3}
                                data-testid="event-desc-input"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Category</Label>
                                <select
                                    value={modal.data.category}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, category: e.target.value } })}
                                    className="w-full h-10 rounded-md border border-input bg-background px-3 mt-1"
                                    data-testid="event-category-select"
                                >
                                    <option value="general">General</option>
                                    <option value="dining">Dining</option>
                                    <option value="workshop">Workshop</option>
                                    <option value="social">Social</option>
                                    <option value="special">Special</option>
                                </select>
                            </div>
                            <div>
                                <Label>Price</Label>
                                <Input
                                    value={modal.data.price}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, price: e.target.value } })}
                                    placeholder="e.g., £15 or Free"
                                    data-testid="event-price-input"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Label>Location</Label>
                            <Input
                                value={modal.data.location}
                                onChange={(e) => setModal({ ...modal, data: { ...modal.data, location: e.target.value } })}
                                placeholder="Burston Garden Centre"
                                data-testid="event-location-input"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Date & Time *</Label>
                                <Input
                                    type="datetime-local"
                                    value={modal.data.date_time ? modal.data.date_time.slice(0, 16) : ''}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, date_time: e.target.value } })}
                                    data-testid="event-datetime-input"
                                />
                            </div>
                            <div>
                                <Label>End Time</Label>
                                <Input
                                    type="datetime-local"
                                    value={modal.data.end_time ? modal.data.end_time.slice(0, 16) : ''}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, end_time: e.target.value } })}
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Contact Phone</Label>
                                <Input
                                    value={modal.data.contact_phone}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, contact_phone: e.target.value } })}
                                    placeholder="01727 832444"
                                />
                            </div>
                            <div>
                                <Label>Contact Email</Label>
                                <Input
                                    value={modal.data.contact_email}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, contact_email: e.target.value } })}
                                    placeholder="info@burston.co.uk"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Label>Event Image</Label>
                            <div className="flex items-center gap-4 mt-2">
                                {modal.data.image_url ? (
                                    <img 
                                        src={modal.data.image_url} 
                                        alt="Event"
                                        className="w-20 h-20 object-cover rounded-lg border"
                                    />
                                ) : (
                                    <div className="w-20 h-20 rounded-lg bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center">
                                        <Image className="w-8 h-8 text-gray-400" />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        ref={eventImageRef}
                                        onChange={handleImageUpload}
                                        accept="image/jpeg,image/png,image/webp,image/gif"
                                        className="hidden"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => eventImageRef.current?.click()}
                                        disabled={uploadingImage}
                                        data-testid="upload-event-image-btn"
                                    >
                                        {uploadingImage ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4 mr-2" />
                                                Upload Image
                                            </>
                                        )}
                                    </Button>
                                    <p className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP, GIF (max 5MB)</p>
                                </div>
                            </div>
                            <div className="mt-2">
                                <Label className="text-xs text-muted-foreground">Or enter URL directly</Label>
                                <Input
                                    value={modal.data.image_url || ''}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, image_url: e.target.value } })}
                                    placeholder="https://www.burston.co.uk/wp-content/uploads/..."
                                    className="mt-1"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Label>Booking URL</Label>
                            <Input
                                value={modal.data.booking_url || ''}
                                onChange={(e) => setModal({ ...modal, data: { ...modal.data, booking_url: e.target.value } })}
                                placeholder="https://www.burston.co.uk/event/..."
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Link to the event page on burston.co.uk for booking
                            </p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={modal.data.is_recurring}
                                onCheckedChange={(checked) => setModal({ ...modal, data: { ...modal.data, is_recurring: checked } })}
                                data-testid="event-recurring-toggle"
                            />
                            <Label>Recurring event</Label>
                        </div>
                    </div>
                )}
                
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>
                        Cancel
                    </Button>
                    <Button onClick={onSave} data-testid="save-event-btn">
                        {modal.data?.id ? 'Update' : 'Create'} Event
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Offer Modal
export function OfferModal({ 
    modal, 
    setModal, 
    onSave, 
    headers,
    uploadingImage,
    setUploadingImage
}) {
    const offerImageRef = useRef(null);

    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF');
            return;
        }
        
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File too large. Maximum size is 5MB');
            return;
        }
        
        setUploadingImage(true);
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axios.post(`${API}/upload/image`, formData, {
                headers: {
                    ...headers,
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
            setModal({ open: true, data: { ...modal.data, image_url: imageUrl }});
            toast.success('Image uploaded successfully');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload image');
        } finally {
            setUploadingImage(false);
        }
    };

    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {modal.data?.id ? 'Edit Offer' : 'Create New Offer'}
                    </DialogTitle>
                    <DialogDescription>
                        {modal.data?.id ? 'Update the offer details below.' : 'Create a new promotional offer for members.'}
                    </DialogDescription>
                </DialogHeader>
                
                {modal.data && (
                    <div className="space-y-4">
                        <div>
                            <Label>Title *</Label>
                            <Input
                                value={modal.data.title}
                                onChange={(e) => setModal({ ...modal, data: { ...modal.data, title: e.target.value } })}
                                placeholder="e.g., 10% Off Restaurant"
                                data-testid="offer-title-input"
                            />
                        </div>
                        
                        <div>
                            <Label>Description *</Label>
                            <Textarea
                                value={modal.data.description}
                                onChange={(e) => setModal({ ...modal, data: { ...modal.data, description: e.target.value } })}
                                placeholder="Describe the offer..."
                                rows={3}
                                data-testid="offer-desc-input"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Category</Label>
                                <select
                                    value={modal.data.category}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, category: e.target.value } })}
                                    className="w-full h-10 rounded-md border border-input bg-background px-3 mt-1"
                                    data-testid="offer-category-select"
                                >
                                    <option value="general">General</option>
                                    <option value="restaurant">Restaurant</option>
                                    <option value="plants">Plants & Garden</option>
                                    <option value="delivery">Delivery</option>
                                    <option value="seasonal">Seasonal</option>
                                </select>
                            </div>
                            <div>
                                <Label>Discount Type</Label>
                                <select
                                    value={modal.data.discount_type}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, discount_type: e.target.value } })}
                                    className="w-full h-10 rounded-md border border-input bg-background px-3 mt-1"
                                >
                                    <option value="percentage">Percentage Off</option>
                                    <option value="fixed">Fixed Amount Off</option>
                                    <option value="free_item">Free Item</option>
                                    <option value="free_delivery">Free Delivery</option>
                                    <option value="bogo">Buy One Get One</option>
                                </select>
                            </div>
                        </div>
                        
                        {(modal.data.discount_type === 'percentage' || modal.data.discount_type === 'fixed') && (
                            <div>
                                <Label>Discount Value {modal.data.discount_type === 'percentage' ? '(%)' : '(£)'}</Label>
                                <Input
                                    type="number"
                                    value={modal.data.discount_value}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, discount_value: parseFloat(e.target.value) || 0 } })}
                                    placeholder={modal.data.discount_type === 'percentage' ? '10' : '5.00'}
                                    data-testid="offer-discount-input"
                                />
                            </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Valid From</Label>
                                <Input
                                    type="date"
                                    value={modal.data.valid_from ? modal.data.valid_from.slice(0, 10) : ''}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, valid_from: e.target.value } })}
                                    data-testid="offer-valid-from-input"
                                />
                            </div>
                            <div>
                                <Label>Valid To</Label>
                                <Input
                                    type="date"
                                    value={modal.data.valid_to ? modal.data.valid_to.slice(0, 10) : ''}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, valid_to: e.target.value } })}
                                    data-testid="offer-valid-to-input"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Label>Terms & Conditions</Label>
                            <Textarea
                                value={modal.data.terms_conditions || ''}
                                onChange={(e) => setModal({ ...modal, data: { ...modal.data, terms_conditions: e.target.value } })}
                                placeholder="e.g., Cannot be combined with other offers..."
                                rows={2}
                            />
                        </div>
                        
                        <div>
                            <Label>Offer Image</Label>
                            <div className="flex items-center gap-4 mt-2">
                                {modal.data.image_url ? (
                                    <img 
                                        src={modal.data.image_url} 
                                        alt="Offer"
                                        className="w-20 h-20 object-cover rounded-lg border"
                                    />
                                ) : (
                                    <div className="w-20 h-20 rounded-lg bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center">
                                        <Image className="w-8 h-8 text-gray-400" />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        ref={offerImageRef}
                                        onChange={handleImageUpload}
                                        accept="image/jpeg,image/png,image/webp,image/gif"
                                        className="hidden"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => offerImageRef.current?.click()}
                                        disabled={uploadingImage}
                                        data-testid="upload-offer-image-btn"
                                    >
                                        {uploadingImage ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4 mr-2" />
                                                Upload Image
                                            </>
                                        )}
                                    </Button>
                                    <p className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP, GIF (max 5MB)</p>
                                </div>
                            </div>
                            <div className="mt-2">
                                <Label className="text-xs text-muted-foreground">Or enter URL directly</Label>
                                <Input
                                    value={modal.data.image_url || ''}
                                    onChange={(e) => setModal({ ...modal, data: { ...modal.data, image_url: e.target.value } })}
                                    placeholder="https://example.com/image.jpg"
                                    className="mt-1"
                                />
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Switch
                                    checked={modal.data.is_members_only}
                                    onCheckedChange={(checked) => setModal({ ...modal, data: { ...modal.data, is_members_only: checked } })}
                                    data-testid="offer-members-only-toggle"
                                />
                                <Label>Members Only</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch
                                    checked={modal.data.is_active}
                                    onCheckedChange={(checked) => setModal({ ...modal, data: { ...modal.data, is_active: checked } })}
                                    data-testid="offer-active-toggle"
                                />
                                <Label>Active</Label>
                            </div>
                        </div>
                    </div>
                )}
                
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>
                        Cancel
                    </Button>
                    <Button onClick={onSave} data-testid="save-offer-btn">
                        {modal.data?.id ? 'Update' : 'Create'} Offer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Credit Adjustment Modal
export function CreditModal({ modal, setModal, onAdjust }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, member: null, amount: '', reason: '' })}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Adjust Credits</DialogTitle>
                    <DialogDescription>
                        Add or deduct credits from a member's balance.
                    </DialogDescription>
                </DialogHeader>
                
                {modal.member && (
                    <div className="space-y-4">
                        <div className="bg-muted p-4 rounded-lg">
                            <p className="font-bold">{modal.member.name}</p>
                            <p className="text-sm text-muted-foreground">{modal.member.email}</p>
                            <p className="text-sm mt-2">
                                Current balance: <span className="font-bold">{modal.member.profile?.credits_balance || 0} credits</span>
                            </p>
                        </div>
                        
                        <div>
                            <Label>Amount (use negative to deduct)</Label>
                            <Input
                                type="number"
                                value={modal.amount}
                                onChange={(e) => setModal({ ...modal, amount: e.target.value })}
                                placeholder="e.g., 5 or -3"
                                data-testid="credit-amount-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Enter a positive number to add credits, negative to remove
                            </p>
                        </div>
                        
                        <div>
                            <Label>Reason</Label>
                            <Input
                                value={modal.reason}
                                onChange={(e) => setModal({ ...modal, reason: e.target.value })}
                                placeholder="e.g., Manual adjustment, Bonus credits"
                                data-testid="credit-reason-input"
                            />
                        </div>
                    </div>
                )}
                
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, member: null, amount: '', reason: '' })}>
                        Cancel
                    </Button>
                    <Button onClick={onAdjust} data-testid="confirm-credit-adjustment-btn">
                        Adjust Credits
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Delete Confirmation Modal
export function DeleteConfirmModal({ modal, setModal, onConfirm }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, type: '', id: null, name: '' })}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogDescription>
                        This action cannot be undone. The item will be permanently removed.
                    </DialogDescription>
                </DialogHeader>
                <p className="text-muted-foreground">
                    Are you sure you want to delete "{modal.name}"?
                </p>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, type: '', id: null, name: '' })}>
                        Cancel
                    </Button>
                    <Button 
                        variant="destructive" 
                        onClick={onConfirm}
                        data-testid="confirm-delete-btn"
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Attendees Modal
export function AttendeesModal({ 
    modal, 
    setModal, 
    onDownloadRegister, 
    onPrintRegister,
    onEmailFacilitator,
    onBulkEmail
}) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, activity: null, attendees: [], loading: false })}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ClipboardList className="w-5 h-5" />
                        Class Register
                    </DialogTitle>
                    <DialogDescription>
                        View, print, or email the attendance list for this activity.
                    </DialogDescription>
                </DialogHeader>
                
                {modal.activity && (
                    <div className="flex-1 overflow-y-auto">
                        <div className="bg-muted p-4 rounded-lg mb-4">
                            <h3 className="font-bold text-lg">{modal.activity.title}</h3>
                            <div className="grid grid-cols-2 gap-2 mt-2 text-sm text-muted-foreground">
                                <span><Clock className="w-3 h-3 inline mr-1" />{formatDateTime(modal.activity.date_time)}</span>
                                <span>{modal.activity.location}</span>
                                <span>Facilitator: {modal.activity.facilitator || 'TBC'}</span>
                                <span>Capacity: {modal.activity.current_bookings || 0}/{modal.activity.capacity}</span>
                            </div>
                        </div>

                        {modal.loading ? (
                            <div className="flex justify-center py-8">
                                <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : modal.attendees.length === 0 ? (
                            <div className="text-center py-8">
                                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground">No attendees yet</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div>
                                    <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                                        <UserCheck className="w-4 h-4 text-emerald-600" />
                                        Confirmed ({modal.attendees.filter(a => a.status === 'confirmed').length})
                                    </h4>
                                    <div className="space-y-2">
                                        {modal.attendees.filter(a => a.status === 'confirmed').map((booking, index) => (
                                            <div key={booking.id} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100" data-testid={`attendee-${booking.id}`}>
                                                <span className="w-6 h-6 rounded-full bg-emerald-600 text-white text-xs flex items-center justify-center font-bold">
                                                    {index + 1}
                                                </span>
                                                <div className="flex-1">
                                                    <p className="font-medium">{booking.member?.name || 'Unknown'}</p>
                                                    <p className="text-xs text-muted-foreground">{booking.member?.email}</p>
                                                </div>
                                                <div className="text-right text-xs text-muted-foreground">
                                                    <p>{booking.member?.membership_number}</p>
                                                    <p>Booked: {formatDateTime(booking.booked_at)}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {modal.attendees.filter(a => a.status === 'waitlist').length > 0 && (
                                    <div>
                                        <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-amber-600" />
                                            Waitlist ({modal.attendees.filter(a => a.status === 'waitlist').length})
                                        </h4>
                                        <div className="space-y-2">
                                            {modal.attendees.filter(a => a.status === 'waitlist').map((booking, index) => (
                                                <div key={booking.id} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                                                    <span className="w-6 h-6 rounded-full bg-amber-600 text-white text-xs flex items-center justify-center font-bold">
                                                        {index + 1}
                                                    </span>
                                                    <div className="flex-1">
                                                        <p className="font-medium">{booking.member?.name || 'Unknown'}</p>
                                                        <p className="text-xs text-muted-foreground">{booking.member?.email}</p>
                                                    </div>
                                                    <div className="text-right text-xs text-muted-foreground">
                                                        <p>{booking.member?.membership_number}</p>
                                                        <p>Joined: {formatDateTime(booking.booked_at)}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                
                <DialogFooter className="border-t pt-4 mt-4 flex-wrap gap-2">
                    <div className="flex gap-2 flex-1">
                        <Button 
                            variant="outline" 
                            onClick={onPrintRegister}
                            disabled={modal.loading}
                            data-testid="print-register-btn"
                        >
                            <Printer className="w-4 h-4 mr-2" />
                            Print
                        </Button>
                        <Button 
                            variant="outline"
                            onClick={onEmailFacilitator}
                            disabled={modal.loading || modal.attendees.length === 0}
                            data-testid="email-facilitator-btn"
                        >
                            <Mail className="w-4 h-4 mr-2" />
                            Email to Facilitator
                        </Button>
                        <Button 
                            variant="outline"
                            onClick={onBulkEmail}
                            disabled={modal.loading || modal.attendees.filter(a => a.status === 'confirmed').length === 0}
                            data-testid="bulk-email-btn"
                        >
                            <Send className="w-4 h-4 mr-2" />
                            Email Attendees
                        </Button>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setModal({ open: false, activity: null, attendees: [], loading: false })}>
                            Close
                        </Button>
                        <Button 
                            onClick={onDownloadRegister} 
                            disabled={modal.loading || modal.attendees.length === 0}
                            data-testid="download-register-btn"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Download CSV
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Email to Facilitator Modal
export function EmailFacilitatorModal({ modal, setModal, onSend, activity }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, email: '', sending: false })}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Email Register to Facilitator
                    </DialogTitle>
                    <DialogDescription>
                        Send the class attendance list to the activity facilitator.
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                    <div className="bg-muted p-3 rounded-lg text-sm">
                        <p className="font-medium">{activity?.title}</p>
                        <p className="text-muted-foreground">{formatDateTime(activity?.date_time)}</p>
                    </div>
                    
                    <div>
                        <Label>Facilitator Email Address</Label>
                        <Input
                            type="email"
                            value={modal.email}
                            onChange={(e) => setModal(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="facilitator@example.com"
                            data-testid="facilitator-email-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            The class register will be sent to this email address
                        </p>
                    </div>
                </div>
                
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, email: '', sending: false })}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={onSend}
                        disabled={modal.sending || !modal.email}
                        data-testid="send-facilitator-email-btn"
                    >
                        {modal.sending ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4 mr-2" />
                                Send Email
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Bulk Email Modal
export function BulkEmailModal({ modal, setModal, onSend, activity, confirmedCount }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, subject: '', message: '', sending: false })}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Send className="w-5 h-5" />
                        Email All Attendees
                    </DialogTitle>
                    <DialogDescription>
                        Send a message to all confirmed attendees of this activity.
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                    <div className="bg-muted p-3 rounded-lg text-sm">
                        <p className="font-medium">{activity?.title}</p>
                        <p className="text-muted-foreground">
                            Sending to {confirmedCount} confirmed attendee(s)
                        </p>
                    </div>
                    
                    <div>
                        <Label>Subject</Label>
                        <Input
                            value={modal.subject}
                            onChange={(e) => setModal(prev => ({ ...prev, subject: e.target.value }))}
                            placeholder="Email subject"
                            data-testid="bulk-email-subject"
                        />
                    </div>
                    
                    <div>
                        <Label>Message</Label>
                        <Textarea
                            value={modal.message}
                            onChange={(e) => setModal(prev => ({ ...prev, message: e.target.value }))}
                            placeholder="Write your message to all attendees..."
                            rows={6}
                            data-testid="bulk-email-message"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            Activity details will be automatically included in the email
                        </p>
                    </div>
                </div>
                
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, subject: '', message: '', sending: false })}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={onSend}
                        disabled={modal.sending || !modal.subject || !modal.message}
                        data-testid="send-bulk-email-btn"
                    >
                        {modal.sending ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4 mr-2" />
                                Send to All
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Manager Modal
export function ManagerModal({ modal, setModal, onSave }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{modal.data?.id ? 'Edit Manager' : 'Create Manager'}</DialogTitle>
                    <DialogDescription>
                        {modal.data?.id ? 'Update manager account details.' : 'Create a new manager account with access to content management.'}
                    </DialogDescription>
                </DialogHeader>
                {modal.data && (
                    <div className="space-y-4">
                        <div>
                            <Label>Name *</Label>
                            <Input 
                                value={modal.data.name} 
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, name: e.target.value }}))}
                                placeholder="Content Manager"
                                data-testid="manager-name-input"
                            />
                        </div>
                        <div>
                            <Label>Email *</Label>
                            <Input 
                                type="email"
                                value={modal.data.email} 
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, email: e.target.value }}))}
                                placeholder="manager@burston.co.uk"
                                data-testid="manager-email-input"
                            />
                        </div>
                        <div>
                            <Label>{modal.data.id ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
                            <Input 
                                type="password"
                                value={modal.data.password} 
                                onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, password: e.target.value }}))}
                                placeholder={modal.data.id ? '••••••••' : 'Enter password'}
                                data-testid="manager-password-input"
                            />
                        </div>
                        {!modal.data.id && (
                            <p className="text-sm text-muted-foreground">
                                The manager will be able to login at <code className="bg-muted px-1 rounded">/login</code> and will be redirected to their management panel.
                            </p>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>Cancel</Button>
                    <Button onClick={onSave} className="bg-primary hover:bg-primary/90" data-testid="save-manager-btn">
                        {modal.data?.id ? 'Update' : 'Create'} Manager
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
